import i18n from '../../i18n/i18n';
import { tr } from '../../i18n/i18n';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { STATUS } from '../../constants';
import { RootState } from '../store/index';
import {
  IPostAttendanceRequestBody,
  IPostAttendanceResponseBody,
  IAttendanceError,
  IAttendance,
  IPageInfo,
  ISummary,
  ICheckinResponseBody,
  ICheckinRequestBody,
  ICheckoutRequestBody,
  ICheckoutResponseBody,
  IPostAbsentRequestBody,
  IAbsentResponseBody,
  IPendingResponseBody,
  IPostPendingRequestBody,
  IFetchCutOffTimeResponseBody,
  IPostAttendancePayload,
  IPostAttendanceResponse,
} from '../../interface/attendanceModel';
import {
  ChekoutAttendance,
  getAttendanceRecord,
  AbsentAttendance,
  PendingAttendance,
  asignAttendanceCutOff,
  fetchAttendanceCutOff,
  updatAttendanceCutOff,
  deletAttendanceCutOff,
} from '../../services/attendance/index';
import api from '../../services/api';
import { deleteAttendance, postAttendance, postAttendanceCheckout } from '../action';

// State interface
interface IAttendanceState {
  loading: boolean;
  error?: IAttendanceError;
  message?: string;
  status?: string;
  attendanceData?: IAttendance[];
  summary?: ISummary;
  pageInfo?: IPageInfo;
}

// Default state
const DEFAULT_STATE: IAttendanceState = {
  loading: false,
  error: undefined,
  message: undefined,
  status: undefined,
  attendanceData: undefined,
  summary: undefined,
  pageInfo: undefined,
};

// Thunk for getting attendance records
export const getAttendanceRecords = createAsyncThunk<
  IPostAttendanceResponseBody,
  IPostAttendanceRequestBody,
  { rejectValue: IAttendanceError }
>('attendance/getAttendanceRecords', async (body, { rejectWithValue }) => {
  try {
    const response = await api.post<IPostAttendanceResponseBody>('/attendance/record', body);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status, message: err.message });
    }
  }
});

// Thunk for posting pending attendance data
export const postPendingAttendance = createAsyncThunk<
  IPendingResponseBody,
  IPostPendingRequestBody,
  { rejectValue: IAttendanceError }
>('attendance/postPendingAttendance', async (body, { rejectWithValue }) => {
  try {
    const response = await PendingAttendance(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const assignAttendanceCutoff = createAsyncThunk<
  IAbsentResponseBody,
  string,
  { rejectValue: IAttendanceError }
>('attendance/assignAttendanceCutoff', async (body, { rejectWithValue }) => {
  try {
    const response = await api.post('/attendance/cutoff', { time: `${body}` });
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const updateAttendanceCutoff = createAsyncThunk<
  IAbsentResponseBody,
  { body: string; id: string },
  { rejectValue: IAttendanceError }
>('attendance/updateAttendanceCutoff', async ({ body, id }, { rejectWithValue }) => {
  try {
    const response = await updatAttendanceCutOff(body, id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const deleteAttendanceCutoff = createAsyncThunk<
  IAbsentResponseBody,
  { id: string },
  { rejectValue: IAttendanceError }
>('attendance/updateAttendanceCutoff', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await deletAttendanceCutOff(id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const fetchAttendanceCutofftime = createAsyncThunk<
  IFetchCutOffTimeResponseBody,
  void,
  { rejectValue: IAttendanceError }
>('attendance/fetchAttendanceCutoff', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchAttendanceCutOff();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

// Thunk for posting absent attendance
export const postAbsentAttendance = createAsyncThunk<
  IAbsentResponseBody,
  IPostAbsentRequestBody,
  { rejectValue: IAttendanceError }
>('attendance/postAbsentAttendance', async (body, { rejectWithValue }) => {
  try {
    const response = await AbsentAttendance(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

// Slice for attendance state management
const attendanceSlice = createSlice({
  name: 'attendance',
  initialState: DEFAULT_STATE,
  reducers: {
    resetAttendanceState: () => DEFAULT_STATE,
  },
  extraReducers: (builder) => {
    builder.addCase(getAttendanceRecords.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      getAttendanceRecords.fulfilled,
      (state, action: PayloadAction<IPostAttendanceResponseBody>) => {
        state.loading = false;
        state.attendanceData = action.payload.data.attendance || [];
        state.summary = action.payload.data.summary;
        state.pageInfo = action.payload.data.page;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      getAttendanceRecords.rejected,
      (state, action: PayloadAction<IAttendanceError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );

    builder.addCase(postAttendance.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      postAttendance.fulfilled,
      (state, action: PayloadAction<IPostAttendanceResponse>) => {
        state.loading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      postAttendance.rejected,
      (state, action: PayloadAction<IAttendanceError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );

    builder.addCase(postAttendanceCheckout.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      postAttendanceCheckout.fulfilled,
      (state, action: PayloadAction<ICheckoutResponseBody>) => {
        state.loading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      postAttendanceCheckout.rejected,
      (state, action: PayloadAction<IAttendanceError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );

    builder.addCase(postPendingAttendance.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      postPendingAttendance.fulfilled,
      (state, action: PayloadAction<IPendingResponseBody>) => {
        state.loading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      postPendingAttendance.rejected,
      (state, action: PayloadAction<IAttendanceError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );

    builder.addCase(postAbsentAttendance.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      postAbsentAttendance.fulfilled,
      (state, action: PayloadAction<IAbsentResponseBody>) => {
        state.loading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      postAbsentAttendance.rejected,
      (state, action: PayloadAction<IAttendanceError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );
    builder.addCase(deleteAttendance.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      deleteAttendance.fulfilled,
      (state, action: PayloadAction<{ message: string }>) => {
        state.loading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      deleteAttendance.rejected,
      (state, action: PayloadAction<IAttendanceError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );
  },
});

// Export actions and reducer
export const { resetAttendanceState } = attendanceSlice.actions;
export const attendanceReducer = attendanceSlice.reducer;

// Selectors
export const selectAttendanceState = (state: RootState): IAttendanceState => state.attendance;
export const selectAttendanceStatus = (state: RootState): string | undefined =>
  state.attendance.status;
export const selectAttendanceError = (state: RootState): IAttendanceError | undefined =>
  state.attendance.error;
export const selectAttendanceMessage = (state: RootState): string | undefined =>
  state.attendance.message;
export const selectAttendanceLoading = (state: RootState): boolean => state.attendance.loading;
export const selectAttendanceData = (state: RootState): IAttendance[] | undefined =>
  state.attendance.attendanceData;
export const selectSummary = (state: RootState): ISummary | undefined => state.attendance.summary;
export const selectPageInfo = (state: RootState): IPageInfo | undefined =>
  state.attendance.pageInfo;

export default attendanceReducer;
